// lib/opencv-tiled-alignment.ts
import type { CV, Mat } from '@techstark/opencv-js';
import type { LayerAlignmentResult } from '#types/types';

/**
 * "Tiled consensus alignment", THE way a layer is realigned on what lies under it. It cuts the pair into a 4x4
 * grid, estimates a TRANSLATION per tile by phase correlation on gradient orientations, then fits one similarity
 * (uniform scale plus translation, the only transform a layer can carry) across the tiles by exhaustive RANSAC.
 * A tile that fell on the edited content disagrees and is dropped; confidence is the agreement between disjoint
 * regions, not the sharpness of one correlation peak, so the method can refuse and say why.
 *
 * It replaced an ORB-in-a-border-band estimator, which answered with whatever RANSAC agreed on and had no way to
 * say it was unsure. That predecessor is gone from lib/, kept only as the benchmark's frozen baseline in
 * scripts/qa/fixtures/alignment-benchmark-border.ts. `npm run bench:alignment` measures this file against it and
 * its report is the only place accuracy figures are stated, because a number written here would go stale
 * silently the first time a threshold moves.
 *
 * The PRIMITIVES below `computeTiledLayerAlignment` are exported for that benchmark only, and nothing else should
 * import them: its frozen witness candidate (D2) shares them rather than owning a second implementation of the
 * same maths, so the working resolution, the structure epsilon and the spectral windows are defined HERE and
 * imported there, and a change to any of them moves both candidates at once. The alignment function itself is
 * ordinary product code, called by lib/opencv-worker-client.ts.
 */

/** Longest side the estimator works on; the answer is scaled back to full resolution. */
export const WORKING_SIZE = 1024;
/** Stride used to sample the gradient magnitudes the epsilon percentile is read from. */
const PERCENTILE_STRIDE = 8;
const PEAK_EXCLUSION_PX = 3;
const STRUCTURE_EPS_PERCENTILE = 0.7;
const LOWPASS_SIGMA_CYCLES = 0.5 / 8;

export type Own = <T extends { delete(): void }>(value: T) => T;

export const withScope = <T>(work: (own: Own) => T): T => {
  const allocated: { delete(): void }[] = [];
  try {
    return work((value) => { allocated.push(value); return value; });
  } finally {
    allocated.forEach((value) => value.delete());
  }
};

const percentile = (values: Float32Array, p: number) => {
  const sorted = Float32Array.from(values).sort();
  return sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))]!;
};

export const blur = (cv: CV, own: Own, source: Mat, sigma: number) => {
  const out = own(new cv.Mat());
  cv.GaussianBlur(source, out, new cv.Size(0, 0), sigma, sigma, cv.BORDER_REFLECT_101);
  return out;
};

const gradients = (cv: CV, own: Own, smoothed: Mat) => {
  const gx = own(new cv.Mat());
  const gy = own(new cv.Mat());
  const magnitude = own(new cv.Mat());
  cv.Sobel(smoothed, gx, cv.CV_32F, 1, 0, 3, 1, 0, cv.BORDER_REFLECT_101);
  cv.Sobel(smoothed, gy, cv.CV_32F, 0, 1, 3, 1, 0, cv.BORDER_REFLECT_101);
  cv.magnitude(gx, gy, magnitude);
  return { gx, gy, magnitude };
};

export const parabola = (left: number, centre: number, right: number) => {
  const denominator = left - 2 * centre + right;
  return denominator < 0 ? Math.max(-0.5, Math.min(0.5, (left - right) / (2 * denominator))) : 0;
};

const sampledPercentile = (values: Float32Array, p: number, stride: number) => {
  const sampled = new Float32Array(Math.ceil(values.length / stride));
  for (let i = 0, j = 0; i < values.length; i += stride, j += 1) sampled[j] = values[i]!;
  return percentile(sampled, p);
};

/** (gx + i·gy)/|∇|, zeroed below ε and windowed, as an interleaved 2-channel Mat. */
export const orientationField = (cv: CV, own: Own, smoothed: Mat, eps: number, window: Mat) => {
  const { gx, gy, magnitude } = gradients(cv, own, smoothed);
  const field = own(new cv.Mat(smoothed.rows, smoothed.cols, cv.CV_32FC2));
  const keep = own(new cv.Mat());
  cv.threshold(magnitude, keep, eps, 1, cv.THRESH_BINARY);
  const denominator = own(new cv.Mat());
  cv.add(magnitude, own(new cv.Mat(magnitude.rows, magnitude.cols, cv.CV_32F, new cv.Scalar(1e-12))), denominator);
  const scale = own(new cv.Mat());
  cv.divide(keep, denominator, scale);
  cv.multiply(scale, window, scale);
  const re = own(new cv.Mat());
  const im = own(new cv.Mat());
  cv.multiply(gx, scale, re);
  cv.multiply(gy, scale, im);
  const planes = own(new cv.MatVector());
  planes.push_back(re);
  planes.push_back(im);
  cv.merge(planes, field);
  const spectrum = own(new cv.Mat());
  cv.dft(field, spectrum, cv.DFT_COMPLEX_OUTPUT);
  return spectrum;
};

/** Peak of the normalised cross-power surface, in signed pixels, with its ratio to the next peak and its PCE. */
export const correlate = (cv: CV, own: Own, fs: Mat, fo: Mat, lowpass: Float32Array, scratch: Float32Array) => {
  const a = fs.data32F;
  const b = fo.data32F;
  for (let i = 0; i < a.length; i += 2) {
    const re = a[i]! * b[i]! + a[i + 1]! * b[i + 1]!;
    const im = a[i + 1]! * b[i]! - a[i]! * b[i + 1]!;
    const norm = Math.sqrt(re * re + im * im);
    if (norm < 1e-12) { scratch[i] = 0; scratch[i + 1] = 0; continue; }
    const weight = lowpass[i / 2]! / norm;
    scratch[i] = re * weight;
    scratch[i + 1] = im * weight;
  }
  const cross = own(new cv.Mat(fs.rows, fs.cols, cv.CV_32FC2));
  cross.data32F.set(scratch);
  const surface = own(new cv.Mat());
  cv.dft(cross, surface, cv.DFT_INVERSE | cv.DFT_SCALE | cv.DFT_COMPLEX_OUTPUT);
  const complex = surface.data32F;
  const width = fs.cols;
  const height = fs.rows;
  let best = 0;
  let bestValue = -Infinity;
  for (let i = 0; i < width * height; i += 1) {
    const value = complex[2 * i]!;
    if (value > bestValue) { bestValue = value; best = i; }
  }
  const bx = best % width;
  const by = (best - bx) / width;
  let second = -Infinity;
  let energy = 0;
  let count = 0;
  for (let i = 0; i < width * height; i += 1) {
    const x = i % width;
    const y = (i - x) / width;
    const dx = Math.min(Math.abs(x - bx), width - Math.abs(x - bx));
    const dy = Math.min(Math.abs(y - by), height - Math.abs(y - by));
    const value = complex[2 * i]!;
    if (dx * dx + dy * dy > PEAK_EXCLUSION_PX * PEAK_EXCLUSION_PX && value > second) second = value;
    if (dx > 2 || dy > 2) { energy += value * value; count += 1; }
  }
  const at = (x: number, y: number) => complex[2 * (((y + height) % height) * width + ((x + width) % width))]!;
  const signed = (value: number, size: number) => (value > size / 2 ? value - size : value);
  return {
    d: [
      signed(bx + parabola(at(bx - 1, by), bestValue, at(bx + 1, by)), width),
      signed(by + parabola(at(bx, by - 1), bestValue, at(bx, by + 1)), height),
    ],
    value: bestValue, second, pce: bestValue ** 2 / (energy / count),
  };
};

/**
 * The epsilon below which a gradient is noise rather than structure, read from this pair's own magnitudes so a
 * flat region cannot manufacture an orientation field out of its grain.
 */
export const structureEpsilon = (cv: CV, own: Own, smoothed: Mat) =>
  sampledPercentile(gradients(cv, own, smoothed).magnitude.data32F, STRUCTURE_EPS_PERCENTILE, PERCENTILE_STRIDE);

/**
 * The two windows a phase correlation of `size` x `size` needs: the Hanning window that kills the edge
 * discontinuity, and the Gaussian low-pass that keeps the peak from being decided by the highest frequencies,
 * which are exactly the ones a generative edit rewrites.
 */
export const spectralWindows = (cv: CV, own: Own, size: number) => {
  const window = own(new cv.Mat());
  // Compiled into the build, missing from its typings.
  (cv as unknown as { createHanningWindow(dst: Mat, size: unknown, type: number): void }).createHanningWindow(window, new cv.Size(size, size), cv.CV_32F);
  const lowpass = new Float32Array(size * size);
  const frequency = (i: number) => (i <= size / 2 ? i : i - size) / size;
  for (let y = 0; y < size; y += 1) for (let x = 0; x < size; x += 1) {
    lowpass[y * size + x] = Math.exp(-(frequency(x) ** 2 + frequency(y) ** 2) / (2 * LOWPASS_SIGMA_CYCLES ** 2));
  }
  return { window, lowpass };
};

/** Grayscale float copy of `image`, area-downscaled by `factor`. Shared by every candidate that caps its resolution. */
export const toWorkingGray = (cv: CV, own: Own, image: ImageData, factor: number) => {
  const rgba = own(cv.matFromImageData(image));
  const gray8 = own(new cv.Mat());
  cv.cvtColor(rgba, gray8, cv.COLOR_RGBA2GRAY);
  const gray = own(new cv.Mat());
  gray8.convertTo(gray, cv.CV_32F);
  if (factor >= 1) return gray;
  const small = own(new cv.Mat());
  cv.resize(gray, small, new cv.Size(Math.round(image.width * factor), Math.round(image.height * factor)), 0, 0, cv.INTER_AREA);
  return small;
};

/** Cells per side of the grid. 4 gives exactly 3 border tiles per side (12) plus 4 interior ones. */
const T1_GRID = 4;

type Side = 'top' | 'right' | 'bottom' | 'left' | 'interior';
const OPPOSITE_SIDE: Partial<Record<Side, Side>> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/**
 * Thresholds fixed before the first T1 run, and where they come from:
 * - minTilePce / minTilePeakRatio: a tile is 1/16 of the working image, and PCE grows with the number of samples
 *   that feed the peak, so the round-4 whole-image floor over the 37 good cases (PCE >= 2280, ratio >= 1.50) is
 *   divided by that factor and rounded down to 200; the ratio is put at 1.20, between round 4's 1.25 and the 1.09
 *   of the flipped copy, because a small tile has fewer samples to separate the first peak from the second.
 * - inlierPx: the benchmark already calls an accepted answer false above 1.5 px at the centre, so a tile whose
 *   displacement disagrees with the consensus by more than that cannot belong to the same rigid scene. Working px.
 * - maxRms: 3. Inliers are already within inlierPx of the fit, so their RMS cannot exceed it: at 3 this check
 *   refuses nothing, and inlierPx is the tolerance that actually bounds the disagreement. Working px.
 * - minInliers: two, because a single tile is not agreement, which is the whole claim of this candidate.
 * - minBaselineFraction: a pair of tiles closer than 0.4 of the short side estimates the scale off too short a
 *   lever arm; 0.4 of 1024 is 410 px, which excludes neighbouring cells (256 px) and keeps everything wider.
 * The bounds on the answer itself (|t|, |s-1|) are NOT here: they belong to the common gate, which applies them to
 * the three candidates alike.
 */
export const T1_ACCEPT = { minTilePce: 200, minTilePeakRatio: 1.2, inlierPx: 1.5, maxRms: 3, minInliers: 2, minBaselineFraction: 0.4 };

/** One tile's own answer: where it says the content moved, and whether it is worth listening to. */
type TileEstimate = { side: Side; cx: number; cy: number; dx: number; dy: number; confident: boolean };


/**
 * Cell centres of a `grid` x `grid` layout, each side owning three of the twelve ring cells (the corner cells are
 * attributed to one side, clockwise) and the centre cells kept as interior tiles for when the border carries no
 * structure. Tiles are square and do not overlap, so two tiles never share a pixel of evidence.
 */
const tileLayout = (width: number, height: number, grid: number) => {
  const cellWidth = width / grid;
  const cellHeight = height / grid;
  const tile = Math.max(32, Math.floor(Math.min(cellWidth, cellHeight) / 16) * 16);
  const cells: { r: number; c: number; side: Side }[] = [];
  for (let c = 0; c <= grid - 2; c += 1) cells.push({ r: 0, c, side: 'top' });
  for (let r = 0; r <= grid - 2; r += 1) cells.push({ r, c: grid - 1, side: 'right' });
  for (let c = 1; c <= grid - 1; c += 1) cells.push({ r: grid - 1, c, side: 'bottom' });
  for (let r = 1; r <= grid - 1; r += 1) cells.push({ r, c: 0, side: 'left' });
  for (let r = 1; r <= grid - 2; r += 1) for (let c = 1; c <= grid - 2; c += 1) cells.push({ r, c, side: 'interior' });
  return cells.map(({ r, c, side }) => {
    const x = Math.max(0, Math.min(width - tile, Math.round((c + 0.5) * cellWidth - tile / 2)));
    const y = Math.max(0, Math.min(height - tile, Math.round((r + 0.5) * cellHeight - tile / 2)));
    return { side, x, y, tile, cx: x + (tile - 1) / 2, cy: y + (tile - 1) / 2 };
  });
};

/** Continuous square copy: a roi Mat reports its parent's stride in this build, so it cannot be read per pixel. */
const cutTile = (cv: CV, own: Own, source: Mat, x: number, y: number, tile: number) => {
  const out = own(new cv.Mat());
  cv.warpAffine(source, out, own(cv.matFromArray(2, 3, cv.CV_64F, [1, 0, x, 0, 1, y])), new cv.Size(tile, tile),
    cv.INTER_NEAREST + cv.WARP_INVERSE_MAP, cv.BORDER_REFLECT_101, new cv.Scalar(0));
  return out;
};

type FitPoint = { px: number; py: number; qx: number; qy: number };
type Model = { s: number; tx: number; ty: number };

/** Least squares q = s·p + t over the points, scale forced to 1 when the side rule gives no lever arm. */
const fitSimilarity = (points: FitPoint[], freeScale: boolean): Model => {
  const mean = (get: (point: FitPoint) => number) => points.reduce((sum, point) => sum + get(point), 0) / points.length;
  const pbx = mean((point) => point.px);
  const pby = mean((point) => point.py);
  const qbx = mean((point) => point.qx);
  const qby = mean((point) => point.qy);
  let numerator = 0;
  let denominator = 0;
  for (const point of points) {
    numerator += (point.px - pbx) * (point.qx - qbx) + (point.py - pby) * (point.qy - qby);
    denominator += (point.px - pbx) ** 2 + (point.py - pby) ** 2;
  }
  const s = freeScale && denominator > 0 ? numerator / denominator : 1;
  return { s, tx: qbx - s * pbx, ty: qby - s * pby };
};

const modelResidual = (model: Model, point: FitPoint) =>
  Math.hypot(model.s * point.px + model.tx - point.qx, model.s * point.py + model.ty - point.qy);

/**
 * Exhaustive RANSAC: every pair with a long enough baseline (a two-point similarity) and every single tile (a pure
 * translation) is a hypothesis, scored by inlier count then by residual sum. Sixteen tiles make 120 pairs, so the
 * enumeration is cheaper than sampling and removes the seed from the result.
 */
const bestConsensus = (points: FitPoint[], minBaseline: number) => {
  const score = (model: Model) => {
    const inliers = points.filter((point) => modelResidual(model, point) <= T1_ACCEPT.inlierPx);
    return { model, inliers, sum: inliers.reduce((total, point) => total + modelResidual(model, point), 0) };
  };
  let best = { model: { s: 1, tx: 0, ty: 0 }, inliers: [] as FitPoint[], sum: Infinity };
  const consider = (candidate: ReturnType<typeof score>) => {
    if (candidate.inliers.length > best.inliers.length || (candidate.inliers.length === best.inliers.length && candidate.sum < best.sum)) best = candidate;
  };
  for (const point of points) consider(score({ s: 1, tx: point.qx - point.px, ty: point.qy - point.py }));
  for (let i = 0; i < points.length; i += 1) for (let j = i + 1; j < points.length; j += 1) {
    if (Math.hypot(points[i]!.px - points[j]!.px, points[i]!.py - points[j]!.py) < minBaseline) continue;
    consider(score(fitSimilarity([points[i]!, points[j]!], true)));
  }
  return best;
};

/** 3 or 4 sides: full similarity. 2 sides: scale only if they face each other. 1 side: translation. 0: refusal. */
const scaleIsEstimable = (sides: Side[]) => sides.length >= 3 || (sides.length === 2 && OPPOSITE_SIDE[sides[0]!] === sides[1]);

/**
 * Maps a pixel of `editedLayer` onto `originalUnderlyingImage` as a 2x3 row-major affine [s, 0, tx, 0, s, ty],
 * the direction alignLayerToUnderlying applies. Both images must already be rendered at the same size.
 */
export const computeTiledLayerAlignment = (
  editedLayer: ImageData,
  originalUnderlyingImage: ImageData,
  cv: CV,
): LayerAlignmentResult => withScope((own) => {
  const sourceImage = originalUnderlyingImage;
  const outputImage = editedLayer;
  if (sourceImage.width !== outputImage.width || sourceImage.height !== outputImage.height) {
    return { matrix: null, refusedFor: ['the layer and the image underneath are not the same size'] };
  }
  const longest = Math.max(sourceImage.width, sourceImage.height);
  const factor = Math.min(1, WORKING_SIZE / longest);
  const workingSource = toWorkingGray(cv, own, sourceImage, factor);
  const workingOutput = toWorkingGray(cv, own, outputImage, factor);
  const smoothSource = blur(cv, own, workingSource, 2);
  const smoothOutput = blur(cv, own, workingOutput, 2);
  // One epsilon for the whole image, deliberately: a per-tile epsilon would manufacture an orientation field out of
  // the noise of a flat tile, and a flat tile must stay silent rather than vote.
  const eps = structureEpsilon(cv, own, smoothSource);
  const layout = tileLayout(smoothSource.cols, smoothSource.rows, T1_GRID);
  const size = layout[0]!.tile;
  const { window, lowpass } = spectralWindows(cv, own, size);
  const scratch = new Float32Array(2 * size * size);

  const tiles: TileEstimate[] = layout.map((cell) => withScope((inner) => {
    const fs = orientationField(cv, inner, cutTile(cv, inner, smoothSource, cell.x, cell.y, size), eps, window);
    const fo = orientationField(cv, inner, cutTile(cv, inner, smoothOutput, cell.x, cell.y, size), eps, window);
    const peak = correlate(cv, inner, fs, fo, lowpass, scratch);
    const peakRatio = peak.value / peak.second;
    // A tile with no structure above epsilon produces an all-zero surface, hence NaN here; it must not pass.
    const confident = Number.isFinite(peak.pce) && Number.isFinite(peakRatio)
      && peak.pce >= T1_ACCEPT.minTilePce && peakRatio >= T1_ACCEPT.minTilePeakRatio;
    return { side: cell.side, cx: cell.cx, cy: cell.cy, dx: peak.d[0]!, dy: peak.d[1]!, confident };
  }));

  const confident = tiles.filter((tile) => tile.confident);
  const points = new Map<FitPoint, TileEstimate>();
  for (const tile of confident) points.set({ px: tile.cx, py: tile.cy, qx: tile.cx + tile.dx, qy: tile.cy + tile.dy }, tile);
  const minBaseline = T1_ACCEPT.minBaselineFraction * Math.min(smoothSource.cols, smoothSource.rows);
  const chosen = bestConsensus([...points.keys()], minBaseline);
  // The model form depends on which sides survive, and the surviving set depends on the model: two bounded passes.
  let model = chosen.model;
  let inlierPoints = chosen.inliers;
  let sides: Side[] = [];
  for (let pass = 0; pass < 2 && inlierPoints.length > 0; pass += 1) {
    sides = [...new Set(inlierPoints.map((point) => points.get(point)!.side))].filter((side) => side !== 'interior');
    model = fitSimilarity(inlierPoints, scaleIsEstimable(sides));
    inlierPoints = [...points.keys()].filter((point) => modelResidual(model, point) <= T1_ACCEPT.inlierPx);
  }
  if (inlierPoints.length > 0) {
    sides = [...new Set(inlierPoints.map((point) => points.get(point)!.side))].filter((side) => side !== 'interior');
    model = fitSimilarity(inlierPoints, scaleIsEstimable(sides));
  }
  const rms = inlierPoints.length > 0
    ? Math.sqrt(inlierPoints.reduce((total, point) => total + modelResidual(model, point) ** 2, 0) / inlierPoints.length)
    : Infinity;

  // The gate an UNATTENDED caller is held to; a manual one applies the fit anyway, see matrixToApply.
  // Worded plainly, because they are what an operator reads in the log when an automatic alignment did nothing.
  const refusedFor: string[] = [];
  if (sides.length === 0) {
    refusedFor.push(`no side of the layer matched the image underneath (${confident.length} of ${tiles.length} regions carried enough detail)`);
  }
  if (inlierPoints.length < T1_ACCEPT.minInliers) {
    refusedFor.push(`only ${inlierPoints.length} region${inlierPoints.length === 1 ? '' : 's'} agreed on a correction, at least ${T1_ACCEPT.minInliers} are needed`);
  }
  if (!(rms <= T1_ACCEPT.maxRms)) {
    refusedFor.push(`the ${inlierPoints.length} matching regions disagree by ${rms.toFixed(2)} px: the difference is not a shift and a zoom, which is all a layer can be moved by`);
  }
  // Working pixels back to full resolution: the scale is dimensionless, the translation is not.
  const fittedMatrix = [model.s, 0, model.tx / factor, 0, model.s, model.ty / factor];
  const consensus = {
    inlierTiles: inlierPoints.length,
    totalTiles: tiles.length,
    sides,
    residualRms: Number.isFinite(rms) ? rms : null,
    scaleEstimated: scaleIsEstimable(sides),
    fittedMatrix,
  };
  return { matrix: refusedFor.length > 0 ? null : fittedMatrix, refusedFor, consensus };
});

/**
 * Which matrix a caller should apply, and the one product decision of this file.
 *
 * `bestEffort` is a manual alignment: the user asked for it, is looking at the layer, judges the result and can
 * undo it in one keystroke. Refusing there is worse than moving imperfectly — a flat border carries no anchor,
 * but it carries no visible transition either, so it does not need to be precise. Whatever was fitted is applied,
 * even from a single region and even when the regions disagree more than the gate tolerates.
 *
 * Without `bestEffort` the alignment runs itself after a generation: nobody is watching, nothing can be undone,
 * and a wrong shift of 30 px would simply ship. That path keeps the full gate, thresholds included.
 *
 * Null on both paths means nothing was fitted at all: no region held, or the two images differ in size.
 */
export const matrixToApply = (result: LayerAlignmentResult, bestEffort: boolean) => {
  if (!bestEffort) return result.matrix;
  return result.consensus && result.consensus.inlierTiles > 0 ? result.consensus.fittedMatrix : null;
};
